import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { formatDate } from '@/utils/dateFormat';
import { 
  CheckSquare, Clock, CheckCircle2, AlertCircle, Play, 
  Calendar, Building2, MessageSquare, ChevronRight 
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  assigned: { label: 'Assigned', color: 'bg-blue-100 text-blue-700', icon: CheckSquare },
  in_progress: { label: 'In Progress', color: 'bg-purple-100 text-purple-700', icon: Play },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700', icon: AlertCircle },
};

export default function Activities() {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      // Fetch activities assigned to this vendor
      const response = await axios.get(`${API}/vendor/activities`);
      setActivities(response.data);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      // For now, show empty state - API will be implemented in P1
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (activityId, newStatus) => {
    try {
      await axios.put(`${API}/vendor/activities/${activityId}/status`, { status: newStatus });
      toast.success(`Activity marked as ${newStatus.replace('_', ' ')}`);
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // Filter activities by status
  const filteredActivities = activities.filter(activity => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') return ['assigned', 'in_progress'].includes(activity.status);
    if (statusFilter === 'completed') return ['completed', 'approved'].includes(activity.status);
    return activity.status === statusFilter;
  });

  // Count by status
  const statusCounts = {
    all: activities.length,
    active: activities.filter(a => ['assigned', 'in_progress'].includes(a.status)).length,
    completed: activities.filter(a => ['completed', 'approved'].includes(a.status)).length,
    overdue: activities.filter(a => a.status === 'overdue').length,
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-stone-400">Loading activities...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="activities-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-manrope text-3xl md:text-4xl font-bold tracking-tight text-stone-900 mb-2">
              My Activities
            </h1>
            <p className="text-stone-500">Work assigned to you by Bella Casa Interiors</p>
          </div>
        </div>

        {/* Status Filter Tabs */}
        <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-full">
          <TabsList className="grid grid-cols-4 w-full max-w-lg">
            <TabsTrigger value="all" className="flex items-center gap-2">
              All ({statusCounts.all})
            </TabsTrigger>
            <TabsTrigger value="active" className="flex items-center gap-2">
              <Play size={14} /> Active ({statusCounts.active})
            </TabsTrigger>
            <TabsTrigger value="completed" className="flex items-center gap-2">
              <CheckCircle2 size={14} /> Done ({statusCounts.completed})
            </TabsTrigger>
            <TabsTrigger value="overdue" className="flex items-center gap-2">
              <AlertCircle size={14} /> Overdue ({statusCounts.overdue})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="apple-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <CheckSquare size={20} className="text-blue-600" />
              <span className="text-2xl font-bold">{statusCounts.all}</span>
            </div>
            <p className="text-sm text-stone-500">Total Activities</p>
          </div>
          <div className="apple-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <Play size={20} className="text-purple-600" />
              <span className="text-2xl font-bold">{statusCounts.active}</span>
            </div>
            <p className="text-sm text-stone-500">In Progress</p>
          </div>
          <div className="apple-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle2 size={20} className="text-green-600" />
              <span className="text-2xl font-bold">{statusCounts.completed}</span>
            </div>
            <p className="text-sm text-stone-500">Completed</p>
          </div>
          <div className="apple-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <AlertCircle size={20} className="text-red-600" />
              <span className="text-2xl font-bold">{statusCounts.overdue}</span>
            </div>
            <p className="text-sm text-stone-500">Overdue</p>
          </div>
        </div>

        {/* Activities List */}
        {filteredActivities.length === 0 ? (
          <div className="apple-card p-16 text-center">
            <CheckSquare size={48} className="mx-auto text-stone-300 mb-4" />
            <p className="text-stone-500">
              {statusFilter === 'all' 
                ? 'No activities assigned yet' 
                : `No ${statusFilter} activities`}
            </p>
            <p className="text-sm text-stone-400 mt-1">
              Activities will appear here when assigned by Bella Casa Interiors
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredActivities.map((activity) => {
              const statusConfig = STATUS_CONFIG[activity.status] || STATUS_CONFIG.pending;
              const StatusIcon = statusConfig.icon;
              const isOverdue = new Date(activity.due_date) < new Date() && 
                               !['completed', 'approved'].includes(activity.status);

              return (
                <div 
                  key={activity.id} 
                  className="apple-card p-6 group hover:shadow-md transition-shadow"
                  data-testid={`activity-${activity.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      {/* Activity Header */}
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-manrope text-lg font-bold text-stone-900">
                          {activity.name}
                        </h3>
                        <Badge className={statusConfig.color}>
                          <StatusIcon size={12} className="mr-1" />
                          {statusConfig.label}
                        </Badge>
                        {isOverdue && (
                          <Badge className="bg-red-100 text-red-700">
                            <AlertCircle size={12} className="mr-1" />
                            Overdue
                          </Badge>
                        )}
                      </div>

                      {/* Activity Details */}
                      <div className="flex flex-wrap gap-4 text-sm text-stone-500">
                        {activity.project_name && (
                          <span className="flex items-center gap-1">
                            <Building2 size={14} />
                            {activity.project_name}
                          </span>
                        )}
                        {activity.due_date && (
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            Due: {formatDate(activity.due_date)}
                          </span>
                        )}
                      </div>

                      {/* Description */}
                      {activity.description && (
                        <p className="text-sm text-stone-600 mt-2 line-clamp-2">
                          {activity.description}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {/* Status Update Buttons */}
                      {activity.status === 'assigned' && (
                        <Button 
                          size="sm" 
                          onClick={() => handleStatusUpdate(activity.id, 'in_progress')}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          <Play size={14} className="mr-1" /> Start
                        </Button>
                      )}
                      {activity.status === 'in_progress' && (
                        <Button 
                          size="sm" 
                          onClick={() => handleStatusUpdate(activity.id, 'completed')}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle2 size={14} className="mr-1" /> Complete
                        </Button>
                      )}

                      {/* Chat Button - Navigate to project chat */}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/projects/${activity.project_id}?tab=chat`)}
                        className="text-stone-600"
                      >
                        <MessageSquare size={14} className="mr-1" /> Chat
                      </Button>

                      {/* View Details */}
                      <Button variant="ghost" size="sm">
                        <ChevronRight size={18} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
